import { useCallback, useEffect, useState } from 'react';
import type { RealtimeChannel } from '@supabase/supabase-js';
import { getDbSupabase } from '../../lib/supabase';
import {
  PORTAL_PRESENCE_LABELS,
  PORTAL_PRESENCE_RELATIVE_TICK_MS,
  PORTAL_PRESENCE_SHORT,
  fetchPortalPresence,
  mergePortalPresenceRow,
  pruneStalePortalPresenceRows,
  removePortalPresenceRow,
  type PortalPresencePortal,
  type PortalPresenceRow,
} from '../../lib/portalPresence';
import { formatLastSeenRelative, formatSingaporeDateTime } from '../../utils/dateTime';

function portalBadgeClass(portal: PortalPresencePortal): string {
  return `online-users__portal online-users__portal--${portal}`;
}

function rowFromRealtimePayload(value: unknown): PortalPresenceRow | null {
  if (!value || typeof value !== 'object') return null;
  const row = value as Partial<PortalPresenceRow>;
  if (!row.auth_id || !row.portal || !row.last_seen_at) return null;
  return row as PortalPresenceRow;
}

export function OnlineUsersPanel() {
  const [rows, setRows] = useState<PortalPresenceRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [live, setLive] = useState(false);
  const [, setRelativeTick] = useState(0);

  const load = useCallback(async () => {
    setError(null);
    try {
      const list = await fetchPortalPresence();
      setRows(list);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load online users.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const relativeTimer = window.setInterval(() => {
      setRelativeTick((tick) => tick + 1);
      setRows((current) => pruneStalePortalPresenceRows(current));
    }, PORTAL_PRESENCE_RELATIVE_TICK_MS);

    return () => window.clearInterval(relativeTimer);
  }, []);

  useEffect(() => {
    const db = getDbSupabase('crm');
    let channel: RealtimeChannel | null = null;
    let fallbackTimer: number | null = null;

    const scheduleFallback = () => {
      if (fallbackTimer != null) window.clearTimeout(fallbackTimer);
      fallbackTimer = window.setTimeout(() => {
        void load();
      }, 120_000);
    };

    channel = db
      .channel('portal-presence-live')
      .on(
        'postgres_changes',
        { event: '*', schema: 'crm', table: 'portal_presence' },
        (payload) => {
          scheduleFallback();
          setRows((current) => {
            if (payload.eventType === 'DELETE') {
              const authId = (payload.old as { auth_id?: string } | null)?.auth_id;
              return authId ? removePortalPresenceRow(current, authId) : current;
            }

            const nextRow = rowFromRealtimePayload(payload.new);
            return nextRow ? mergePortalPresenceRow(current, nextRow) : current;
          });
        },
      )
      .subscribe((status) => {
        setLive(status === 'SUBSCRIBED');
        if (status === 'SUBSCRIBED') scheduleFallback();
      });

    return () => {
      if (fallbackTimer != null) window.clearTimeout(fallbackTimer);
      if (channel) void db.removeChannel(channel);
      setLive(false);
    };
  }, [load]);

  return (
    <section className="card settings-panel online-users-panel">
      <div className="settings-panel__head">
        <div>
          <h2 className="settings-panel__title">Who is online</h2>
        </div>
        <div className="online-users-panel__actions">
          {live ? (
            <span className="online-users__live" title="Connected to live updates">
              <span className="online-users__live-dot" aria-hidden />
              Live
            </span>
          ) : null}
          <span className="settings-panel__count">{rows.length} online</span>
          <button type="button" className="btn-secondary btn-sm" onClick={() => void load()}>
            Refresh
          </button>
        </div>
      </div>

      {error ? <div className="alert alert-error">{error}</div> : null}

      {loading ? (
        <div className="settings-loading">
          <div className="empty-state__spinner" />
          <p>Loading online users…</p>
        </div>
      ) : rows.length === 0 ? (
        <p className="settings-empty">No one is online right now.</p>
      ) : (
        <div className="settings-table-wrap">
          <table className="settings-table online-users-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>System</th>
                <th>Last active</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.auth_id}>
                  <td>
                    <span className="online-users__name">
                      <span className="online-users__dot" aria-hidden />
                      {row.display_name?.trim() || '—'}
                    </span>
                  </td>
                  <td>{row.email?.trim() || '—'}</td>
                  <td>
                    <span
                      className={portalBadgeClass(row.portal)}
                      title={PORTAL_PRESENCE_LABELS[row.portal]}
                    >
                      {PORTAL_PRESENCE_SHORT[row.portal]}
                    </span>
                  </td>
                  <td title={formatSingaporeDateTime(row.last_seen_at)}>
                    {formatLastSeenRelative(row.last_seen_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
